"use client";

import React, { useMemo, useState } from 'react';
import {
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PauseCircleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import type { PendingInputAction, PendingInputItem, PendingInputKind } from '@/lib/agentPendingInput';

type NeedsInputTrayProps = {
  items: PendingInputItem[];
  onAction: (item: PendingInputItem, action: PendingInputAction) => void;
  className?: string;
};

const KIND_LABELS: Record<PendingInputKind, string> = {
  clarification: 'Clarification',
  artifact_review: 'Draft review',
  approval_gate: 'Approval gate',
  resumable_run: 'Resume',
};

const ACTION_LABELS: Record<PendingInputAction, string> = {
  resume: 'Resume',
  approve: 'Approve',
  request_changes: 'Request changes',
  discuss: 'Discuss',
  open_artifact: 'Open artifact',
  view_details: 'Details',
};

const KIND_ICONS: Record<PendingInputKind, React.ComponentType<{ className?: string }>> = {
  clarification: ChatBubbleLeftRightIcon,
  artifact_review: DocumentTextIcon,
  approval_gate: ClipboardDocumentCheckIcon,
  resumable_run: PauseCircleIcon,
};

function shortId(value?: string): string | null {
  if (!value) return null;
  return value.length > 16 ? `${value.slice(0, 7)}...${value.slice(-5)}` : value;
}

export function NeedsInputTray({ items, onAction, className }: NeedsInputTrayProps) {
  const [open, setOpen] = useState(false);
  const highPriorityCount = useMemo(() => items.filter((item) => item.priority === 'high').length, [items]);
  const nextItem = items[0];
  if (!items.length) return null;

  return (
    <section
      className={cn(
        'mx-3 mb-2 rounded-lg border border-amber-300/50 bg-amber-50/90 p-2 shadow-sm shadow-amber-950/5',
        'dark:border-amber-700/60 dark:bg-amber-950/35',
        className,
      )}
      aria-label="Needs input"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-amber-300/70 bg-background/80 text-amber-700 dark:border-amber-700/80 dark:text-amber-300">
            <ExclamationTriangleIcon className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <p className="text-sm font-semibold text-foreground">Needs input</p>
              {nextItem ? (
                <span className="hidden truncate text-xs text-muted-foreground sm:inline">
                  Next: {nextItem.title}
                </span>
              ) : null}
            </div>
            <p className="truncate text-[11px] text-muted-foreground">
              {items.length} queued {items.length === 1 ? 'item' : 'items'}
              {highPriorityCount ? ` · ${highPriorityCount} high priority` : ''}
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 shrink-0 px-2 text-xs"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls="needs-input-queue"
        >
          {open ? (
            <>
              Hide <ChevronUpIcon className="h-3.5 w-3.5" />
            </>
          ) : (
            <>
              Review <ChevronDownIcon className="h-3.5 w-3.5" />
            </>
          )}
        </Button>
      </div>

      {open ? (
        <div id="needs-input-queue" className="mt-2 max-h-60 space-y-1.5 overflow-y-auto pr-1">
          {items.map((item) => {
            const Icon = KIND_ICONS[item.kind];
            return (
              <div
                key={item.id}
                className="rounded-lg border border-border/50 bg-background/85 p-2"
              >
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <Icon className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" />
                      <span className="rounded-full border border-amber-300/60 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:border-amber-700 dark:text-amber-300">
                        {KIND_LABELS[item.kind]}
                      </span>
                      <p className="min-w-0 flex-1 truncate text-sm font-medium leading-snug text-foreground">{item.title}</p>
                    </div>
                    <p className="mt-1 truncate text-xs text-muted-foreground">{item.description}</p>
                    <p className="mt-0.5 truncate text-xs text-foreground">{item.actionNeeded}</p>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                      {shortId(item.runId) ? <span>Run {shortId(item.runId)}</span> : null}
                      {shortId(item.requestId) ? <span>Request {shortId(item.requestId)}</span> : null}
                      {item.artifactTitle ? <span className="truncate">Artifact {item.artifactTitle}</span> : null}
                      {item.status ? <span>Status {item.status.replace(/_/g, ' ')}</span> : null}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-1.5">
                    {item.actions.map((action) => (
                      <Button
                        key={`${item.id}:${action}`}
                        type="button"
                        variant={action === 'approve' ? 'primary' : action === 'request_changes' ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => onAction(item, action)}
                      >
                        {ACTION_LABELS[action]}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
